import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons';
import { keyBy } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { DragDropContext, Droppable, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import styled from 'styled-components';

import DraggableListItem from 'dashboard/components/DraggableListItem';
import ReactSelect from 'dashboard/components/ReactSelect';
import { reorder } from 'dashboard/lib/dnd/utils';
import { Workflow } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import { Label } from 'design-system/components/Text';

const DeleteButton = styled(Button)`
  background: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: auto;

  svg {
    color: ${({ theme }) => theme.color.text.tertiary};
  }
`;

const StyledX = styled(XIcon)`
  height: 16px;
`;

interface CaseActionEditTableProps {
  actionWorkflows: Workflow[];
  currentActionIds: string[];
  onConfirm: (actionIds: string[]) => void;
}

export const CaseActionEditTable = ({
  actionWorkflows,
  currentActionIds,
  onConfirm,
}: CaseActionEditTableProps) => {
  const workflowsMap = keyBy('id', actionWorkflows);

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    onConfirm(reorder(currentActionIds, source.index, destination.index));
  };

  const actionsDnd = currentActionIds.map((actionId, idx) => {
    const key = `${actionId} - ${idx}`;
    const handleDelete = () => onConfirm(currentActionIds.filter((_, i) => i !== idx));

    return (
      <DraggableListItem
        key={key}
        draggableId={key}
        index={idx}
        disableDrag={false}
        dataTest={`case-action-edit-table__draggable-item--${actionId}`}
        suffix={
          <DeleteButton
            id={`case-action-edit-table__draggable-item--${actionId}-delete`}
            data-test={`case-action-edit-table__draggable-item--${actionId}-delete`}
            width='icon'
            variant='basic'
            type='button'
            tabIndex={-1}
            onClick={handleDelete}
          >
            <StyledX />
          </DeleteButton>
        }
      >
        <Label size='lg' weight='bold'>
          {workflowsMap[actionId]?.attributes?.name}
        </Label>
      </DraggableListItem>
    );
  });

  return (
    <>
      {actionsDnd.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='case-action-edit-table-dnd__droppable-selected'>
            {(provided) => {
              return (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {actionsDnd}
                  {provided.placeholder}
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
      )}
      <AddCaseActionRow
        data-test='case-action-edit-table-dnd__add-action'
        actionWorkflows={actionWorkflows}
        currentActionIds={currentActionIds}
        onConfirm={onConfirm}
      />
    </>
  );
};

const InputContainer = styled('div')`
  display: flex;
  gap: ${Spacing[5]};
`;

interface AddCaseActionRowProps {
  actionWorkflows: Workflow[];
  currentActionIds: string[];
  onConfirm: (actionIds: string[]) => void;
}

const AddCaseActionRow = ({
  actionWorkflows,
  currentActionIds,
  onConfirm,
}: AddCaseActionRowProps) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [selectedActionId, setSelectedActionId] = useState<string>(undefined);

  const actionOptions = useMemo(() => {
    return (actionWorkflows || [])
      .filter((action) => !currentActionIds.includes(action.id))
      .map((workflow) => {
        return {
          label: workflow.attributes.name,
          value: workflow.id,
        };
      });
  }, [actionWorkflows, currentActionIds]);

  return (
    <InputContainer>
      <ReactSelect
        name='action'
        onChange={(actionId: string) => setSelectedActionId(actionId)}
        placeholder='Action...'
        value={selectedActionId}
        data-test='case-action-edit-table__add-action-input'
        options={actionOptions}
        portal={true}
        isClearable={true}
      />

      <Button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onConfirm([...currentActionIds, selectedActionId]);
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          setSelectedActionId(undefined);
        }}
        id='case-action-edit-table__add-action'
        data-test='case-action-edit-table__add-action'
        width='auto'
        variant='basic'
        disabled={selectedActionId == null}
      >
        Add Action
      </Button>
    </InputContainer>
  );
};

export default CaseActionEditTable;
